import { RSSFeedItem } from '../services/usGovernmentIntegrations';
import { stripHtml } from './htmlToPlainText';

export const INDUSTRY_NEWS_SEARCH_PARAM = 'q';

export interface HighlightSegment {
  text: string;
  highlight: boolean;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function normalizeIndustryNewsSearchQuery(query: string): string {
  return query.trim();
}

export function getIndustryNewsSearchTokens(query: string): string[] {
  return normalizeIndustryNewsSearchQuery(query)
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
}

export function getArticleSearchHaystack(
  article: Pick<RSSFeedItem, 'title' | 'description'>
): string {
  return `${article.title ?? ''} ${stripHtml(article.description)}`.toLowerCase();
}

export function articleMatchesIndustryNewsSearch(
  article: Pick<RSSFeedItem, 'title' | 'description'>,
  query: string
): boolean {
  const tokens = getIndustryNewsSearchTokens(query);
  if (tokens.length === 0) {
    return true;
  }

  const haystack = getArticleSearchHaystack(article);
  return tokens.every((token) => haystack.includes(token));
}

export function filterIndustryNewsArticles<T extends Pick<RSSFeedItem, 'title' | 'description'>>(
  articles: T[],
  query: string
): T[] {
  const tokens = getIndustryNewsSearchTokens(query);
  if (tokens.length === 0) {
    return articles;
  }

  return articles.filter((article) => articleMatchesIndustryNewsSearch(article, query));
}

export function splitTextBySearchHighlight(
  text: string,
  query: string
): HighlightSegment[] {
  if (!text) {
    return [{ text: '', highlight: false }];
  }

  const tokens = getIndustryNewsSearchTokens(query);
  if (tokens.length === 0) {
    return [{ text, highlight: false }];
  }

  const pattern = new RegExp(`(${tokens.map(escapeRegExp).join('|')})`, 'gi');
  const segments: HighlightSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ text: text.slice(lastIndex, match.index), highlight: false });
    }
    segments.push({ text: match[0], highlight: true });
    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), highlight: false });
  }

  return segments.length > 0 ? segments : [{ text, highlight: false }];
}

export function formatIndustryNewsSearchResultsLabel(count: number, query: string): string {
  const normalized = normalizeIndustryNewsSearchQuery(query);
  if (!normalized) {
    return '';
  }

  const noun = count === 1 ? 'result' : 'results';
  return `${count} ${noun} for '${normalized}'`;
}
